<template>
  <div class="media-carousel">
    <div
      ref="carouselWrapper"
      class="scroll-container"
      @scroll="handleScroll"
    >
      <div class="scroll-wrapper">
        <media-element
          v-for="(item, index) in blocks"
          :key="item.id"
          class="scroll-item"
          :image="item.image"
          :image-desktop="item.image_desktop"
          :video="item.video"
          :video-desktop="item.video_desktop"
          :video-external-url="item.video_external_url"
          :video-external-desktop-url="item.video_external_desktop_url"
          :autoplay-video="item.autoplay"
          :loop-video="item.loop"
          :js-scroll-item="index"
        />
      </div>
    </div>
    <div
      v-if="hasIndicator"
      class="indicator-container"
    >
      <button
        v-for="(item, index) in blocks"
        :key="'indicator-' + index"
        class="indicator-wrapper"
        @click="handleClick(index)"
      >
        <span :class="['indicator', { active: currentIndex === index }]" />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Component: MediaCarousel
 * ------------------------------------------------------------------------------
 * - Component for a carousel that displays a group of media items
 * @param {String} id - The unique identifier for the carousel.
 * @param {Array} blocks - An array of media blocks to display in the carousel.
 * @param {Boolean} autoplay - Whether the carousel should autoplay. Default is true.
 * @param {Number}  autoplaySpeed - Interval in milliseconds for autoplay.
 * @param {Boolean} loop - Whether the carousel should loop back to the beginning after the last slide.
 */

import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'
import MediaElement from '~components/media-element/media-element'

export default {
  name: 'MediaCarousel',

  components: {
    MediaElement,
  },

  props: {
    id: {
      type: String,
      default: '',
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    autoplaySpeed: {
      type: Number,
      default: 3000,
    },
    loop: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentIndex: 0,
      autoplayInterval: null,
      userInteracted: false,
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    slides() {

      /**
       * Note: querySelectorAll in Vue is an anti-pattern, but this.$refs was inconsistent.
       */
      return [...this.$refs.carouselWrapper.querySelectorAll('[js-scroll-item]')]
    },

    hasIndicator() {
      return this.blocks.length > 1
    },
  },

  mounted() {
    this.startAutoplay()
  },

  beforeUnmount() {
    this.stopAutoplay()
  },

  methods: {
    handleScroll(event) {
      const scrollContainer = event.target
      const scrollPosition = scrollContainer.scrollLeft
      const containerWidth = scrollContainer.clientWidth

      const newIndex = Math.round(scrollPosition / containerWidth)
      this.currentIndex = newIndex
    },

    /**
     * Start the autoplay functionality.
     */
    startAutoplay() {
      if (this.userInteracted || !this.autoplay || this.isMobile) {
        return
      }

      this.autoplayInterval = setInterval(() => {
        this.nextSlide()
      }, this.autoplaySpeed)
    },

    /**
     * Stop the autoplay functionality.
     */
    stopAutoplay() {
      clearInterval(this.autoplayInterval)
    },

    /**
     * Move to a specific slide.
     * @param {Number} index - The index of the slide to go to.
     */
    goToSlide(index) {
      const targetSlide = this.slides[index]

      if (!targetSlide) {
        return
      }

      const scrollOffset = targetSlide.offsetLeft - this.$refs.carouselWrapper.offsetLeft
      this.$refs.carouselWrapper.scrollLeft = scrollOffset
    },

    /**
     * Move to a specific slide and stop autoplay.
     * @param {Number} index - The index of the slide to go to.
     */
    handleClick(index) {
      this.userInteracted = true
      this.goToSlide(index)
      this.stopAutoplay()
    },

    /**
     * Move to the next slide in the carousel.
     */
    nextSlide() {
      if (this.currentIndex < this.blocks.length - 1) {
        this.goToSlide(this.currentIndex + 1)

        return
      }

      if (this.loop) {
        this.goToSlide(0)
      }
    },
  },
}
</script>

<style src="./media-carousel.scss" lang="scss"></style>
